import { useEffect, useRef, useState } from "react";

interface GapStats {
  totalDays: number;
  gapUpDays: number;
  gapDownDays: number;
  avgGapUp: number;
  avgGapDown: number;
  winRate: number;
  totalReturn: number;
  avgReturn: number;
}

interface DailyGap {
  date: string;
  prevClose: number;
  open: number;
  close: number;
  gap: number;
  gapPercent: number;
  isGapUp: boolean;
}

const TV_SYMBOL = "NASDAQ:MU";

// Fetch daily OHLCV data for MU
async function fetchMUGapData(): Promise<DailyGap[]> {
  try {
    // Using Yahoo Finance with longer range
    const response = await fetch(
      "https://query1.finance.yahoo.com/v8/finance/chart/MU?interval=1d&range=5y&events=history"
    );
    const data = await response.json();
    
    const result = data?.chart?.result?.[0];
    if (!result) return [];
    
    const timestamps = result.timestamp as number[];
    const quote = result.indicators?.quote?.[0];
    if (!timestamps || !quote) return [];
    
    const gaps: DailyGap[] = [];
    
    for (let i = 1; i < timestamps.length; i++) {
      const prevClose = quote.close[i - 1];
      const currOpen = quote.open[i];
      const currClose = quote.close[i];
      
      if (prevClose === null || currOpen === null || currClose === null) continue;
      
      const gap = currOpen - prevClose;
      const gapPercent = (gap / prevClose) * 100;
      
      gaps.push({
        date: new Date(timestamps[i] * 1000).toISOString().split("T")[0],
        prevClose: prevClose,
        open: currOpen,
        close: currClose,
        gap,
        gapPercent,
        isGapUp: gap > 0,
      });
    }
    
    return gaps.reverse(); // Most recent first
  } catch (error) {
    console.error("Error fetching MU data:", error);
    return [];
  }
}

function calculateStats(gaps: DailyGap[]): GapStats {
  if (gaps.length === 0) {
    return {
      totalDays: 0,
      gapUpDays: 0,
      gapDownDays: 0,
      avgGapUp: 0,
      avgGapDown: 0,
      winRate: 0,
      totalReturn: 0,
      avgReturn: 0,
    };
  }

  const gapUpDays = gaps.filter(g => g.isGapUp).length;
  const gapDownDays = gaps.filter(g => !g.isGapUp).length;
  
  const gapUps = gaps.filter(g => g.isGapUp);
  const gapDowns = gaps.filter(g => !g.isGapUp);
  
  const avgGapUp = gapUps.length > 0 
    ? gapUps.reduce((sum, g) => sum + g.gapPercent, 0) / gapUps.length 
    : 0;
  
  const avgGapDown = gapDowns.length > 0 
    ? gapDowns.reduce((sum, g) => sum + g.gapPercent, 0) / gapDowns.length 
    : 0;

  // Strategy: Buy at previous close, sell at current open
  // Profit/loss = (open - previous_close) / previous_close * 100
  const returns = gaps.map(g => g.gapPercent);
  const totalReturn = returns.reduce((sum, r) => sum + r, 0);
  const avgReturn = totalReturn / returns.length;
  
  // Win rate: positive return days / total days
  const winDays = returns.filter(r => r > 0).length;
  const winRate = (winDays / returns.length) * 100;

  return {
    totalDays: gaps.length,
    gapUpDays,
    gapDownDays,
    avgGapUp,
    avgGapDown,
    winRate,
    totalReturn,
    avgReturn,
  };
}

function MUChart() {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevSymbol = useRef("");

  useEffect(() => {
    if (prevSymbol.current === TV_SYMBOL) return;
    prevSymbol.current = TV_SYMBOL;
    
    if (!containerRef.current) return;
    containerRef.current.innerHTML = "";

    const initWidget = () => {
      // @ts-ignore
      if (!window.TradingView) return;
      // @ts-ignore
      new window.TradingView.widget({
        autosize: true,
        symbol: TV_SYMBOL,
        interval: "D",
        timezone: "America/New_York",
        theme: "dark",
        style: "1",
        locale: "en",
        container_id: "mu-gap-chart",
        hide_top_toolbar: false,
        allow_symbol_change: false,
        studies: ["RSI@tv-basicstudies", "MACD@tv-basicstudies"],
      });
    };

    // @ts-ignore
    if (window.TradingView) {
      initWidget();
    } else {
      const existing = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
      if (!existing) {
        const script = document.createElement("script");
        script.src = "https://s3.tradingview.com/tv.js";
        script.async = true;
        script.onload = initWidget;
        document.head.appendChild(script);
      } else {
        existing.addEventListener("load", initWidget);
      }
    }
  }, []);

  return (
    <div
      id="mu-gap-chart"
      ref={containerRef}
      style={{ width: "100%", height: 450 }}
      className="rounded-xl border border-gray-700"
    />
  );
}

export default function MUGapStrategy() {
  const [gaps, setGaps] = useState<DailyGap[]>([]);
  const [stats, setStats] = useState<GapStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllDays, setShowAllDays] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const gapData = await fetchMUGapData();
      setGaps(gapData);
      setStats(calculateStats(gapData));
      setLoading(false);
    };
    loadData();
  }, []);

  const displayGaps = showAllDays ? gaps : gaps.slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Chart Section */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            📈 MU (Micron Technology) - Gap Strategy
          </h2>
          <span className="text-sm text-gray-400">Buy Close → Sell Open</span>
        </div>
        <MUChart />
      </div>

      {/* Strategy Stats */}
      {stats && !loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
            <p className="text-gray-400 text-xs uppercase tracking-wide">Total Trading Days</p>
            <p className="text-2xl font-bold text-white">{stats.totalDays}</p>
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
            <p className="text-gray-400 text-xs uppercase tracking-wide">Win Rate</p>
            <p className={`text-2xl font-bold ${stats.winRate >= 50 ? "text-green-400" : "text-red-400"}`}>
              {stats.winRate.toFixed(1)}%
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
            <p className="text-gray-400 text-xs uppercase tracking-wide">Total Return</p>
            <p className={`text-2xl font-bold ${stats.totalReturn >= 0 ? "text-green-400" : "text-red-400"}`}>
              {stats.totalReturn >= 0 ? "+" : ""}{stats.totalReturn.toFixed(2)}%
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
            <p className="text-gray-400 text-xs uppercase tracking-wide">Avg Return/Day</p>
            <p className={`text-2xl font-bold ${stats.avgReturn >= 0 ? "text-green-400" : "text-red-400"}`}>
              {stats.avgReturn >= 0 ? "+" : ""}{stats.avgReturn.toFixed(2)}%
            </p>
          </div>
        </div>
      )}

      {/* Gap Details */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">📊 Gap Analysis (Last {displayGaps.length} Days)</h3>
          {gaps.length > 10 && (
            <button
              onClick={() => setShowAllDays(!showAllDays)}
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              {showAllDays ? "Show Less" : `Show All (${gaps.length})`}
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-gray-400">Loading gap data...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-2 px-3 text-gray-400 font-medium">Date</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Prev Close</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Open</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Close</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Gap $</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Gap %</th>
                  <th className="text-center py-2 px-3 text-gray-400 font-medium">Direction</th>
                </tr>
              </thead>
              <tbody>
                {displayGaps.map((gap, i) => (
                  <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="py-2 px-3 text-gray-300">{gap.date}</td>
                    <td className="py-2 px-3 text-right text-gray-300 font-mono">${gap.prevClose.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right text-gray-300 font-mono">${gap.open.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right text-gray-300 font-mono">${gap.close.toFixed(2)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${gap.gap >= 0 ? "text-green-400" : "text-red-400"}`}>
                      {gap.gap >= 0 ? "+" : ""}${gap.gap.toFixed(2)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-bold ${gap.gapPercent >= 0 ? "text-green-400" : "text-red-400"}`}>
                      {gap.gapPercent >= 0 ? "+" : ""}{gap.gapPercent.toFixed(2)}%
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        gap.isGapUp 
                          ? "bg-green-900/50 text-green-400" 
                          : "bg-red-900/50 text-red-400"
                      }`}>
                        {gap.isGapUp ? "↑ Gap Up" : "↓ Gap Down"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Stats */}
        {stats && (
          <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-gray-500 text-xs">Gap Up Days</p>
              <p className="text-green-400 font-semibold">{stats.gapUpDays} ({((stats.gapUpDays / stats.totalDays) * 100).toFixed(1)}%)</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Avg Gap Up</p>
              <p className="text-green-400 font-semibold">+{stats.avgGapUp.toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Gap Down Days</p>
              <p className="text-red-400 font-semibold">{stats.gapDownDays} ({((stats.gapDownDays / stats.totalDays) * 100).toFixed(1)}%)</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Avg Gap Down</p>
              <p className="text-red-400 font-semibold">{stats.avgGapDown.toFixed(2)}%</p>
            </div>
          </div>
        )}
      </div>

      {/* Strategy Explanation */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
        <h3 className="text-lg font-semibold mb-3">🎯 Strategy: Buy Close, Sell Open</h3>
        <div className="text-gray-300 text-sm space-y-2">
          <p>
            <strong className="text-white">How it works:</strong> Buy MU at the close of each trading day, 
            then sell at the open of the next trading day.
          </p>
          <p>
            <strong className="text-white">Rationale:</strong> This captures overnight moves and gap openings. 
           MU is known for significant after-hours movement due to semiconductor industry news cycles.
          </p>
          <p>
            <strong className="text-white">Historical Performance:</strong> Over the last {stats?.totalDays || 0} trading days,
            this strategy has produced a {stats?.totalReturn && stats.totalReturn >= 0 ? "positive" : "negative"} return of{" "}
            <span className={stats?.totalReturn && stats.totalReturn >= 0 ? "text-green-400" : "text-red-400"}>
              {stats?.totalReturn?.toFixed(2)}%
            </span>{" "}
            with a <span className={stats?.winRate && stats.winRate >= 50 ? "text-green-400" : "text-red-400"}>
              {stats?.winRate?.toFixed(1)}%
            </span> win rate.
          </p>
        </div>
      </div>
    </div>
  );
}